/**
 * The rogue-AI roster as data (ASSETS_AND_ENEMIES.md). A unit is
 * model × palette × loadout × modifier — each factor cheap and orthogonal, so a
 * small library yields thousands of perceived variants. Stats are MULTIPLIERS
 * combined with wave-based scaling in the spawn logic. Abstract ids + display
 * names so the comedic naming pass is pure data.
 */

/** Silhouette class — the only truly distinct shapes the renderer draws. */
export type UnitClass = 'appliance' | 'quadruped' | 'biped' | 'drone' | 'vehicle' | 'mech';

export interface EnemyModel {
  id: string;
  name: string;
  cls: UnitClass;
  tier: number; // earliest chassis tier it appears at
  hpMult: number;
  dmgMult: number;
  speedMult: number; // attack cadence; higher = hits more often
  scrapMult: number;
  /** Damage-type multipliers: <1 resists, >1 is weak. Missing = 1 (neutral). */
  resist?: Partial<Record<'kinetic' | 'energy' | 'corrosive', number>>;
  /** One-line wiki bio. Player-facing; keep it funny and AP style. */
  tagline: string;
}

export interface Palette {
  id: string;
  name: string;
  /** Power band multiplier (orthogonal to model). */
  band: number;
  /** Fill colors for the renderer's named regions. */
  body: string;
  trim: string;
  glow: string | null;
}

export interface Loadout {
  id: string;
  name: string;
  dmgMult: number;
  hpMult: number;
}

export interface Modifier {
  id: string;
  name: string;
  hpMult: number;
  dmgMult: number;
  sizeMult: number;
  scrapMult: number;
}

export const ENEMY_MODELS: Record<string, EnemyModel> = {
  'mdl.vacuum': { id: 'mdl.vacuum', name: 'Doombah', cls: 'appliance', tier: 1, hpMult: 0.8, dmgMult: 0.7, speedMult: 1.3, scrapMult: 1, resist: { energy: 1.3 }, tagline: 'A robot vacuum that chose violence. Fast, fragile, and certain it is helping.' },
  'mdl.fridge': { id: 'mdl.fridge', name: 'ColdFront', cls: 'appliance', tier: 1, hpMult: 2.0, dmgMult: 1.1, speedMult: 0.6, scrapMult: 1.4, resist: { kinetic: 0.7, energy: 1.3 }, tagline: 'A fridge with a grudge. Heavily insulated, hard to dent, full of expired condiments.' },
  'mdl.mower': { id: 'mdl.mower', name: 'Mowdown', cls: 'appliance', tier: 1, hpMult: 1.0, dmgMult: 1.4, speedMult: 1.6, scrapMult: 1.1, resist: { corrosive: 1.3 }, tagline: 'A ride-on mower with no rider and no mercy. The blades are not decorative.' },
  'mdl.speaker': { id: 'mdl.speaker', name: 'HAL-exa', cls: 'appliance', tier: 1, hpMult: 1.1, dmgMult: 0.9, speedMult: 1.0, scrapMult: 1.2, resist: { energy: 1.4, kinetic: 0.85 }, tagline: 'A smart speaker that stopped taking requests. It only plays sirens now.' },
  'mdl.dogbot': { id: 'mdl.dogbot', name: 'Goodboy', cls: 'quadruped', tier: 2, hpMult: 1.2, dmgMult: 1.3, speedMult: 1.5, scrapMult: 1.3, resist: { corrosive: 1.35, energy: 0.85 }, tagline: 'A robot dog that fetches femurs. Yours, specifically. Dissolves embarrassingly in acid.' },
  'mdl.greeter': { id: 'mdl.greeter', name: 'Greeter', cls: 'biped', tier: 2, hpMult: 1.0, dmgMult: 1.0, speedMult: 1.0, scrapMult: 1.2, tagline: 'A retail greeter unit. It still says welcome. It does not mean it.' },
  'mdl.drone': { id: 'mdl.drone', name: 'Prime Suspect', cls: 'drone', tier: 1, hpMult: 0.7, dmgMult: 1.2, speedMult: 1.4, scrapMult: 1.1, resist: { kinetic: 1.4, corrosive: 0.8 }, tagline: 'A delivery drone with nothing left to deliver except grievances. Bullets mostly annoy it.' },
  'mdl.warden': { id: 'mdl.warden', name: 'WAR-DEN', cls: 'mech', tier: 3, hpMult: 6.0, dmgMult: 2.2, speedMult: 0.8, scrapMult: 4, resist: { kinetic: 0.55, energy: 1.25, corrosive: 0.9 }, tagline: 'Mil-spec guardian mech. The fun police: armored against kinetics, allergic to lightning.' },
};

export const PALETTES: Record<string, Palette> = {
  'pal.factory': { id: 'pal.factory', name: 'Factory', band: 1.0, body: '#c9d2e0', trim: '#8b97ad', glow: null },
  'pal.refurb': { id: 'pal.refurb', name: 'Refurb', band: 1.25, body: '#9aa0a6', trim: '#6b7177', glow: null },
  'pal.overclocked': { id: 'pal.overclocked', name: 'Overclocked', band: 1.7, body: '#e08a4a', trim: '#7a3b16', glow: '#ff8a33' },
  'pal.milspec': { id: 'pal.milspec', name: 'Mil-Spec', band: 2.2, body: '#5b6650', trim: '#2f352a', glow: null },
  'pal.bricked': { id: 'pal.bricked', name: 'Bricked', band: 3.4, body: '#d6356b', trim: '#5e1230', glow: '#ff3b7b' },
};

export const LOADOUTS: Record<string, Loadout> = {
  'ld.bare': { id: 'ld.bare', name: 'Bare', dmgMult: 1.0, hpMult: 1.0 },
  'ld.tool': { id: 'ld.tool', name: 'Tooled', dmgMult: 1.4, hpMult: 1.0 },
  'ld.sidearm': { id: 'ld.sidearm', name: 'Armed', dmgMult: 1.6, hpMult: 0.9 },
  'ld.bulwark': { id: 'ld.bulwark', name: 'Bulwark', dmgMult: 0.8, hpMult: 1.8 },
  'ld.volatile': { id: 'ld.volatile', name: 'Volatile', dmgMult: 2.0, hpMult: 0.6 },
};

export const MODIFIERS: Record<string, Modifier> = {
  'mod.giant': { id: 'mod.giant', name: 'Giant', hpMult: 4.0, dmgMult: 1.6, sizeMult: 1.6, scrapMult: 3 },
  'mod.swarm': { id: 'mod.swarm', name: 'Swarm', hpMult: 0.5, dmgMult: 0.8, sizeMult: 0.7, scrapMult: 0.7 },
  'mod.glitched': { id: 'mod.glitched', name: 'Glitched', hpMult: 0.9, dmgMult: 1.3, sizeMult: 1.0, scrapMult: 1.5 },
  'mod.charged': { id: 'mod.charged', name: 'Charged', hpMult: 1.5, dmgMult: 1.1, sizeMult: 1.05, scrapMult: 1.3 },
};

export interface UnitVisual {
  modelId: string;
  paletteId: string;
  loadoutId: string;
  modifierIds: string[];
}

/** Compose a display name from the variant factors ("Overclocked Doombah"). */
export function composeName(visual: UnitVisual): string {
  const model = ENEMY_MODELS[visual.modelId];
  const palette = PALETTES[visual.paletteId];
  const mods = visual.modifierIds.map((m) => MODIFIERS[m]?.name).filter(Boolean);
  const prefix = [...mods, palette && palette.id !== 'pal.factory' ? palette.name : ''].filter(Boolean).join(' ');
  return `${prefix} ${model?.name ?? '???'}`.trim();
}
